import { Button } from 'shared/ui/button'
import { LogOut, Shield, HardHat, Menu, PanelLeftClose, PanelLeftOpen, Building2 } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from 'shared/ui/tooltip'
import { useSystemBranding } from 'shared/branding/SystemBrandingProvider'

interface EptwHeaderProps {
  currentUser: any
  selectedSite?: { name?: string }
  onToggleMobileMenu: () => void
  onToggleSidebar?: () => void
  isSidebarCollapsed?: boolean
  onBackToModules?: () => void
  onLogout: () => void
}

export function EptwHeader({
  currentUser,
  selectedSite,
  onToggleMobileMenu,
  onToggleSidebar,
  isSidebarCollapsed,
  onBackToModules,
  onLogout
}: EptwHeaderProps) {
  const { branding } = useSystemBranding()

  const contractorAccessEndDateLabel = (() => {
    if (currentUser?.userType !== 'contractor' || !currentUser.endDate) {
      return null
    }
    const parsed = new Date(currentUser.endDate)
    if (Number.isNaN(parsed.getTime())) {
      return null
    }
    const day = String(parsed.getDate()).padStart(2, '0')
    const month = String(parsed.getMonth() + 1).padStart(2, '0')
    const year = parsed.getFullYear() + 543
    return `สามารถเข้าใช้งานได้ถึง ${day}.${month}.${year}`
  })()

  return (
    <div className="bg-white shadow-sm border-b p-4">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            className="lg:hidden"
            onClick={onToggleMobileMenu}
          >
            <Menu className="w-4 h-4" />
          </Button>

          {onToggleSidebar && (
            <Button
              variant="ghost"
              size="sm"
              className="hidden lg:flex"
              onClick={onToggleSidebar}
              title={isSidebarCollapsed ? 'ขยายเมนู' : 'หุบเมนู'}
            >
              {isSidebarCollapsed ? (
                <PanelLeftOpen className="w-4 h-4" />
              ) : (
                <PanelLeftClose className="w-4 h-4" />
              )}
            </Button>
          )}

          {onBackToModules && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onBackToModules}
              className="text-blue-600 hover:text-blue-800"
            >
              ← Modules
            </Button>
          )}

          {selectedSite?.name ? (
            <div className="flex items-center gap-2 bg-blue-50 px-3 py-1 rounded-lg">
              <Building2 className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-medium text-blue-900">{selectedSite.name}</span>
            </div>
          ) : null}

          <div className="lg:hidden">
            <div className="flex items-center gap-2">
              {branding.logoUrl ? (
                <img src={branding.logoUrl} alt={branding.systemName} className="h-8 w-8 rounded object-contain" />
              ) : null}
              <h1 className="font-bold text-gray-900">{branding.systemName}</h1>
            </div>
          </div>
        </div>

        {currentUser ? (
          <div className="flex items-center gap-4">
            <div className="hidden min-w-0 max-w-[32rem] text-right sm:block">
              <div className="flex items-center gap-2 text-sm text-gray-600 mb-1">
                {currentUser.userType === 'contractor' ? (
                  <HardHat className="w-4 h-4 text-orange-500" />
                ) : (
                  <Shield className="w-4 h-4 text-blue-500" />
                )}
                <span className="truncate">Welcome, {currentUser.name ?? currentUser.username}</span>
                <span
                  className={`text-xs px-2 py-1 rounded-full ${
                    currentUser.userType === 'contractor'
                      ? 'bg-orange-100 text-orange-700'
                      : 'bg-blue-100 text-blue-700'
                  }`}
                >
                  {currentUser.userType === 'contractor' ? 'Contractor' : 'Employee'}
                </span>
              </div>
              {contractorAccessEndDateLabel ? (
                <div className="mb-1 text-xs text-orange-700">{contractorAccessEndDateLabel}</div>
              ) : null}
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="truncate text-xs text-gray-500 cursor-default">
                    {currentUser.tenantName ?? selectedSite?.name ?? 'No site'}
                  </div>
                </TooltipTrigger>
                <TooltipContent side="bottom" align="end">
                  <div>{currentUser.roleLabel ?? currentUser.role ?? 'User'}</div>
                </TooltipContent>
              </Tooltip>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={onLogout}
              className="text-red-600 border-red-200 hover:bg-red-50"
            >
              <LogOut className="w-4 h-4 mr-1" />
              <span className="hidden sm:inline">Logout</span>
            </Button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
